/**
 * Turning an optimizer suggestion into an edit-campaign request.
 *
 * `POST /api/v2/campaigns/:id/edit` takes a plain-English `description` plus the
 * target entity ids, and the Python editor parses the description to decide what
 * to change. Building that string is therefore load-bearing, not cosmetic — and
 * it was implemented three times over (the campaign details page, the campaign
 * grid panel, and now the optimization agent), which is how the two existing
 * copies drifted apart:
 *
 *   CampaignDetailPage grouped `keyword_targeting` keywords BY MATCH TYPE, so
 *   "EXACT match: …; BROAD match: …" reached the editor intact.
 *   CampaignGridPage flattened the same array to a bare comma list, dropping
 *   every match type — so the editor fell back to its default and exact-match
 *   keywords silently became broad.
 *
 * This module is the single implementation, and it keeps the match-type-aware
 * behaviour. Both pages now call it.
 *
 * Suggestion shapes it has to cover (the optimizer emits per platform):
 *   · Google Ads  — action_type + target_type + target_id + current/recommended_value
 *   · MadHive     — action_type + entity_type + entity_name (+ line_item_id, creative_id)
 *   · Facebook / Reddit — a ready-made `description`
 *   · anything else — `title`, then `rationale`, as last resorts
 */

/**
 * Entity ids the edit should target. `target_entities` arrives as either bare id
 * strings or objects keyed differently per platform, and Google Ads carries its
 * single target in `target_id` instead.
 */
export function extractTargetEntities(suggestion = {}) {
  let targetEntities = [];
  if (Array.isArray(suggestion.target_entities) && suggestion.target_entities.length > 0) {
    targetEntities = suggestion.target_entities
      .map((e) => {
        if (typeof e === 'string') return e;
        if (e && typeof e === 'object') return e.id || e.adgroup_id || e.ad_id || e.ad_set_id;
        return null;
      })
      .filter(Boolean);
  }
  if (suggestion.target_id && suggestion.target_type) targetEntities.push(suggestion.target_id);
  return targetEntities;
}

/**
 * MadHive edits need the entity chain as structured data rather than prose —
 * `null` for every other platform.
 */
export function buildEntityContext(suggestion = {}, destination, platformCampaignId) {
  if (String(destination || '').toLowerCase() !== 'madhive') return null;
  return {
    campaign_id: suggestion.campaign_id || platformCampaignId,
    line_item_id: suggestion.line_item_id || null,
    creative_id: suggestion.creative_id || null,
  };
}

/**
 * The `add keywords …` instruction, with match type preserved.
 *
 * recommended_value is an array of { keyword, match_type } (or bare strings).
 * Grouping by match type is what stops the editor defaulting everything to
 * BROAD — see the module comment.
 */
function keywordDescription(suggestion) {
  const value = suggestion.recommended_value;
  if (!Array.isArray(value)) return `add keywords "${value}"`;

  const byMatch = {};
  for (const entry of value) {
    const kw = (entry && typeof entry === 'object') ? entry : { keyword: entry };
    if (!kw.keyword) continue;
    const matchType = String(kw.match_type || 'BROAD').toUpperCase();
    (byMatch[matchType] = byMatch[matchType] || []).push(kw.keyword);
  }
  const clauses = Object.entries(byMatch).map(
    ([matchType, keywords]) => `${matchType} match: ${keywords.map((k) => `"${k}"`).join(', ')}`,
  );
  return `add keywords to ad group ${suggestion.target_id}: ${clauses.join('; ')}`;
}

/**
 * A human-readable rendering of a suggestion value for the edit description.
 *
 * The optimizer's audience_signal / asset / search_theme recommendations carry
 * an ARRAY OF OBJECTS in `recommended_value`. A raw template-literal
 * interpolation of that array calls `String([{…}, {…}])`, which yields the
 * useless "[object Object],[object Object]" that reaches the editor. Extract a
 * sensible text field per item (or JSON as a last resort) so the description
 * carries the actual audience / asset / theme text.
 */
function describeValue(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === 'object') {
          return item.audience || item.name || item.text || item.keyword
            || item.search_theme || item.headline || item.description
            || JSON.stringify(item);
        }
        return String(item);
      })
      .join(', ');
  }
  if (typeof value === 'object') {
    return value.audience || value.name || value.text || value.keyword
      || value.search_theme || value.headline || value.description
      || JSON.stringify(value);
  }
  return String(value);
}

/**
 * The edit description, or null when the suggestion carries nothing usable.
 *
 * Returns null rather than throwing so callers can report "this suggestion is
 * not applicable" per-row instead of failing a whole batch — the optimization
 * agent needs exactly that to build a preview with a `refused` list.
 */
export function buildEditDescription(suggestion = {}) {
  if (suggestion.description) return suggestion.description;

  if (suggestion.action_type && suggestion.target_type && suggestion.target_id) {
    if (suggestion.action_type === 'keyword_targeting') return keywordDescription(suggestion);
    return `${suggestion.action_type.replace(/_/g, ' ')} ${suggestion.target_type} ${suggestion.target_id}`
      + ` from "${describeValue(suggestion.current_value)}" to "${describeValue(suggestion.recommended_value)}"`;
  }

  if (suggestion.action_type && suggestion.entity_type && suggestion.entity_name) {
    return `${suggestion.action_type.replace(/_/g, ' ')} ${suggestion.entity_type} ${suggestion.entity_name}`
      + ` from "${describeValue(suggestion.current_value)}" to "${describeValue(suggestion.recommended_value)}"`;
  }

  if (suggestion.title) return suggestion.title;
  if (suggestion.rationale) return suggestion.rationale;
  return null;
}

/** The phrase for one edit, without its targets. */
function editVerb(edit, values) {
  const quoted = values.map((v) => `"${v}"`).join(', ');
  return {
    set_field: `Set ${edit.field} to ${quoted}`,
    list_add: `Add ${quoted} to ${edit.field}`,
    list_remove: `Remove ${quoted} from ${edit.field}`,
    list_edit: `Change ${edit.field} entries to ${quoted}`,
  }[edit.operation] || `Set ${edit.field} to ${quoted}`;
}

/**
 * A DV360 pacing recommendation that the optimizer VALIDATED into a field-edit
 * payload (see campaign_optimizer/dv360_optimizer_apply.py). The payload's values are
 * already in the exact format the DV360 field editor parses ("5/1 day", "ASAP/DAILY",
 * a bare host), so they are passed through verbatim rather than re-derived from
 * current/recommended_value — re-derivation is what drifts from what was validated.
 *
 * The whole payload goes as a BATCH. It used to describe `op.edits[0]` and drop the
 * rest, so a nine-domain expansion applied one domain and reported success. The batch
 * route carries the edits structurally in metadata (bypassing the LLM message
 * analyzer), which is both complete and deterministic; the description that rides
 * along is for the chat transcript, not for parsing.
 */
function buildFromEditOperation(op, suggestion = {}, entityLabel = 'line item') {
  const edits = op.edits.filter(Boolean);
  const allTargets = [...new Set(
    edits.flatMap((e) => (Array.isArray(e.target_entities) ? e.target_entities : []))
      .filter(Boolean),
  )];

  // Group by field+operation so the sentence reads "Add "a.com", "b.com" to urls"
  // rather than repeating the verb once per value.
  const groups = new Map();
  edits.forEach((edit) => {
    const key = `${edit.field}|${edit.operation}`;
    if (!groups.has(key)) groups.set(key, { edit, values: [] });
    groups.get(key).values.push(edit.value);
  });
  const phrases = [...groups.values()].map(({ edit, values }) => editVerb(edit, [...new Set(values)]));

  const description = allTargets.length
    ? `${phrases.join('; ')} on ${entityLabel}${allTargets.length === 1 ? '' : 's'} ${allTargets.join(', ')}`
    : phrases.join('; ');

  return {
    description,
    targetEntities: allTargets,
    entityContext: null,
    suggestedValues: null,
    // Structured edits for POST /campaigns/:id/edit's batch path. Field names are
    // the batch contract's (newValue), not the executor's wire format.
    edits: edits.map((edit, index) => {
      // The executor only needs the requested value, but undoing a scalar set
      // also needs the value that existed before Apply Change. Focused
      // optimizer recommendations carry it as current_value; preserve it in
      // the batch envelope so the server can persist an exact inverse.
      const oldValue = edit.old_value !== undefined
        ? edit.old_value
        : (edit.current_value !== undefined ? edit.current_value : suggestion.current_value);
      return {
        editId: `${op.recommendation_id || 'edit'}-${index}`,
        field: edit.field,
        operation: edit.operation,
        value: edit.value,
        ...(edit.new_value !== undefined ? { newValue: edit.new_value } : {}),
        ...(edit.operation === 'set_field' && oldValue !== undefined && oldValue !== null
          ? { oldValue } : {}),
        targetEntities: Array.isArray(edit.target_entities) ? edit.target_entities : [],
        ...(edit.row_context ? { rowContext: edit.row_context } : {}),
      };
    }),
  };
}

/**
 * A Google Ads recommendation the optimizer VALIDATED into a typed payload
 * (see campaign_optimizer/sem_performance_levers.py).
 *
 * The payload is exactly what GoogleAdsUpdateAPIOperations.process_update_payload
 * takes, so it rides through as `configPayload` — the route's existing
 * deterministic channel, which the Python editor executes verbatim without the
 * LLM. That matters most for the creative operations: `buildEditDescription`
 * below flattens a suggestion into ONE ENGLISH SENTENCE for an LLM to re-derive
 * a mutation from, and a sentence cannot carry fifteen headlines. Sending ad
 * copy that way would publish whatever the parser guessed.
 *
 * The description that rides along is for the chat transcript, not for parsing.
 */
const SEM_CONFIG_OPERATIONS = new Set([
  'demandgen_audience', 'demandgen_creative', 'demandgen_channels', 'demandgen_optimized_targeting',
  'ad_copy', 'ad_extension', 'bidding_strategy_switch', 'ad_schedule',
  'network_settings', 'language_targeting', 'geography_targeting',
  'ad_landing_page', 'final_url_expansion',
]);

function semEditDescription(op) {
  const adGroups = Array.isArray(op.ad_groups) ? op.ad_groups : [];
  const campaigns = Array.isArray(op.campaigns) ? op.campaigns : [];

  if (op.operation?.startsWith('demandgen_')) {
    const labels = {
      demandgen_audience: 'Clone and update the audience for',
      demandgen_creative: 'Create a paused creative variant for',
      demandgen_channels: 'Update channel selection for',
      demandgen_optimized_targeting: 'Update optimized targeting for',
    };
    return adGroups.map((g) => `${labels[op.operation]} ad group ${g.id}`).join('; ');
  }
  if (op.operation === 'ad_copy') {
    return adGroups.map((g) => {
      const counts = `${(g.headlines || []).length} headlines, ${(g.descriptions || []).length} descriptions`;
      if (g.action === 'pause') return `Pause ad ${g.replaces_ad_id} in ad group ${g.id}`;
      if (g.action === 'replace') {
        return `Add a responsive search ad to ad group ${g.id} (${counts}), replacing ad ${g.replaces_ad_id}`;
      }
      return `Add a responsive search ad to ad group ${g.id} (${counts})`;
    }).join('; ');
  }

  return [
    ...campaigns.map((c) => [c, 'campaign']),
    ...adGroups.map((g) => [g, 'ad group']),
  ].map(([entry, level]) => {
    const verb = entry.action === 'remove' ? 'Remove' : 'Add';
    const count = (entry.assets || []).length;
    return `${verb} ${count} ${entry.asset_type} asset${count === 1 ? '' : 's'} on ${level} ${entry.id}`;
  }).join('; ');
}

function buildFromSemEditOperation(op, suggestion) {
  const description = semEditDescription(op);
  return {
    description: description || `Apply ${op.operation}`,
    targetEntities: extractTargetEntities(suggestion),
    entityContext: null,
    suggestedValues: null,
    // Executed verbatim by google_ads_edit_campaign_reasoning's deterministic
    // config_payload branch — no prose in the middle.
    configPayload: { operation: op.operation, payload: op },
  };
}

/**
 * Everything `POST /campaigns/:id/edit` needs for one suggestion, or
 * { error } when no description can be built.
 */
export function buildSuggestionEditRequest(suggestion, { destination, platformCampaignId }) {
  const ttdOp = suggestion?.ttd_edit_operation;
  if (String(destination || '').toLowerCase() === 'ttd' && ttdOp
      && suggestion.auto_applicable === true && suggestion.advisory !== true
      && Array.isArray(ttdOp.edits) && ttdOp.edits.length > 0) {
    return buildFromEditOperation(ttdOp, suggestion, 'ad group');
  }
  // A campaign-audit card. The audit validates its own payload (ids read off
  // the live campaign, not parsed out of prose), so it rides the same batch
  // channel the optimizer's DV360 edits do. Gated on auto_applicable so an
  // advisory card can never apply even if it carried a payload.
  const auditOp = suggestion?.audit_operation;
  if (auditOp && Array.isArray(auditOp.edits) && auditOp.edits.length > 0
      && suggestion.auto_applicable === true && suggestion.advisory !== true) {
    // "pause campaign 57567793", not "pause line item 57567793" — the id is a
    // campaign id, and a description naming the wrong entity is what a reader
    // checks the apply against.
    const label = auditOp.operation === 'campaign_status' ? 'campaign' : 'line item';
    return buildFromEditOperation(auditOp, suggestion, label);
  }

  const op = suggestion?.dv360_edit_operation;
  if (op && Array.isArray(op.edits) && op.edits.length > 0) {
    return buildFromEditOperation(op, suggestion);
  }
  const semOp = suggestion?.sem_edit_operation;
  if (semOp && SEM_CONFIG_OPERATIONS.has(semOp.operation)) {
    return buildFromSemEditOperation(semOp, suggestion);
  }
  const description = buildEditDescription(suggestion);
  if (!description) {
    return { error: 'Unable to construct edit description from suggestion' };
  }
  return {
    description,
    targetEntities: extractTargetEntities(suggestion),
    entityContext: buildEntityContext(suggestion, destination, platformCampaignId),
    suggestedValues: suggestion.suggested_values || null,
  };
}

export default {
  buildEditDescription,
  extractTargetEntities,
  buildEntityContext,
  buildSuggestionEditRequest,
};

export { semEditDescription };
